import React, { useEffect, useMemo, useState } from 'react';
import { IdCardIcon } from '@radix-ui/react-icons';
import { Select } from '../../../../components';
import type { SelectOption } from '../../../../components';
import { leadService } from '../../../../services/leadService';
import { useToastStore } from '../../../../store/toastStore';
import type { Lead } from '../../../../types/lead';
import { formatApiError } from '../../../../utils/errorHandler';
import { formatLeadIdLabel } from '../constants';
import { SidebarField } from './SidebarField';

interface TaskLeadFieldProps {
  leadId: string;
  leadUniqueId?: string;
  leadUserId?: string;
  canEditLead: boolean;
  onChange: (
    leadId: string,
    leadUniqueId?: string | null,
    leadUserId?: string | null
  ) => void;
  onLeadMetaResolved?: (meta: {
    leadUniqueId?: string | null;
    leadUserId?: string | null;
  }) => void;
  onLeadClick?: () => void;
}

const formatLeadOptionLabel = (lead: Lead): string => {
  const displayId = lead.uniqueId ?? lead.leadId;
  return `${lead.fullName} (${displayId})`;
};

const toLeadUserId = (lead: Lead): string | null =>
  lead.convertedUserId != null ? String(lead.convertedUserId) : null;

export const TaskLeadField: React.FC<TaskLeadFieldProps> = ({
  leadId,
  leadUniqueId,
  leadUserId,
  canEditLead,
  onChange,
  onLeadMetaResolved,
  onLeadClick,
}) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [lockedLeadLabel, setLockedLeadLabel] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!canEditLead) return undefined;

    let cancelled = false;

    const loadLeads = async () => {
      setIsLoading(true);
      try {
        const response = await leadService.searchLeads({ page: 1, limit: 100 });
        if (cancelled) return;
        setLeads(response.data);
      } catch (err) {
        if (cancelled) return;
        useToastStore.getState().showErrorMessage({
          title: 'Failed to load leads',
          message: formatApiError(err).trim() || 'Unable to load leads.',
        });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadLeads();

    return () => {
      cancelled = true;
    };
  }, [canEditLead]);

  useEffect(() => {
    if (canEditLead || !leadId) {
      setLockedLeadLabel(null);
      return undefined;
    }

    let cancelled = false;

    const loadLead = async () => {
      try {
        const lead = await leadService.getLeadById(leadId);
        if (cancelled) return;
        setLockedLeadLabel(formatLeadOptionLabel(lead));
        onLeadMetaResolved?.({
          leadUniqueId: lead.uniqueId ?? leadUniqueId ?? null,
          leadUserId: toLeadUserId(lead),
        });
      } catch (err) {
        if (!cancelled) {
          const fallbackId = leadUniqueId || leadId;
          setLockedLeadLabel(
            formatLeadIdLabel(fallbackId)?.replace('LEAD ID: ', '') ?? String(fallbackId)
          );
          useToastStore.getState().showErrorMessage({
            title: 'Failed to load lead',
            message: formatApiError(err).trim() || 'Unable to load lead.',
          });
        }
      }
    };

    loadLead();

    return () => {
      cancelled = true;
    };
  }, [canEditLead, leadId, leadUniqueId, onLeadMetaResolved]);

  const options = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'None' },
      ...leads.map((lead) => ({
        value: String(lead.leadId),
        label: formatLeadOptionLabel(lead),
      })),
    ],
    [leads]
  );

  const handleLeadChange = (value: string) => {
    if (!value) {
      onChange('', null, null);
      return;
    }

    const selectedLead = leads.find((lead) => String(lead.leadId) === value);
    onChange(value, selectedLead?.uniqueId ?? null, selectedLead ? toLeadUserId(selectedLead) : null);
  };

  if (!canEditLead) {
    if (!leadId) return null;

    const displayId = leadUniqueId || leadId;
    const label = lockedLeadLabel ?? formatLeadIdLabel(displayId) ?? displayId;
    const canNavigate = Boolean(onLeadClick && (leadUserId || leadUniqueId || leadId));

    return (
      <SidebarField label="Lead" icon={<IdCardIcon aria-hidden="true" />}>
        {canNavigate ? (
          <button
            type="button"
            className="task-details-modal__lead-readonly task-details-modal__lead-link"
            onClick={onLeadClick}
          >
            {label}
          </button>
        ) : (
          <p className="task-details-modal__lead-readonly">{label}</p>
        )}
      </SidebarField>
    );
  }

  return (
    <SidebarField label="Lead" icon={<IdCardIcon aria-hidden="true" />}>
      <Select
        value={leadId}
        onChange={handleLeadChange}
        options={options}
        placeholder={isLoading ? 'Loading leads…' : 'Select lead (optional)'}
        searchable
        disabled={isLoading}
        className="task-details-modal__select"
      />
    </SidebarField>
  );
};
